import { useMemo } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { usePreQuote } from "./usePreQuote";
import { useQuote } from "./useQuote";
import type {
  IEstimateOption,
  IPreQuoteRequest,
  IQuoteResponse,
  QuoteAnchor,
} from "../services/api";
import {
  availableFundingCategories,
  availableFundingMethods,
  categoryFromFundingMethod,
  filterPreQuoteOptions,
  mapPaymentTypeToMethod,
  resolveActiveRoute,
  resolvePayerCode,
  resolvePayoutMethod,
} from "../utils/catalog";
import type {
  ActiveRoute,
  FundingCategory,
  FundingMethod,
} from "../utils/catalog";

export interface TransferEconomics {
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  /** True while the numbers come from the indicative pre-quote (not binding). */
  estimated: boolean;
}

export interface TransferPricing {
  route: ActiveRoute;
  /** Canonical payout method fixed by the claim link (e.g. `CASH_PICKUP`). */
  payoutMethod: string | undefined;
  /** Canonical payer fixed by the claim link (e.g. `ELEKTRA`), if pinned. */
  payerCode: string | undefined;
  /** Parsed pinned amount (per `anchor`), or null when empty/invalid. */
  amount: number | null;
  /** Which leg `amount` is pinned to. */
  anchor: QuoteAnchor;

  preQuoteLoading: boolean;
  preQuoteError: Error | null;
  /** Pre-quote options narrowed to the fixed payout (+ payer). */
  options: IEstimateOption[];
  availableFundingMethods: FundingMethod[];
  availableCategories: Set<FundingCategory>;
  /** Best option whose funding maps to a given UI category. */
  optionForCategory: (category: FundingCategory) => IEstimateOption | undefined;
  /** Best option overall (used for an estimate before a method is picked). */
  bestOption: IEstimateOption | null;

  /** Option matching the currently selected payment method, if any. */
  selectedOption: IEstimateOption | null;
  selectedFundingMethod: FundingMethod | string | null;
  capabilityKey: string | null;

  quote: IQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: Error | null;
  refetchQuote: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;

  /** Display economics: binding quote when available, else the best estimate. */
  economics: TransferEconomics | null;
}

function rankOptions(
  options: IEstimateOption[],
  anchor: QuoteAnchor
): IEstimateOption[] {
  return [...options].sort((a, b) => {
    if (anchor === "receive") {
      // Cheapest to send wins.
      if (a.sendAmount !== b.sendAmount) return a.sendAmount - b.sendAmount;
      return a.customerFee - b.customerFee;
    }
    // Most received (then lowest fee) wins for send-anchored.
    if (a.receiveAmount !== b.receiveAmount) {
      return b.receiveAmount - a.receiveAmount;
    }
    return a.customerFee - b.customerFee;
  });
}

/**
 * Single source of truth for the m2m quote screen pricing. It:
 *  1. Resolves the active corridor + currencies + the payout method/payer the
 *     claim link fixed.
 *  2. Runs an indicative pre-quote scoped to that payout method, using the
 *     last-edited amount anchor (`send` | `receive`).
 *  3. Narrows the returned capabilities to the fixed payout (+ payer) and
 *     exposes the funding methods/categories that are actually available — this
 *     is what drives the "Pagando con" choices.
 *  4. Maps the selected payment method to a concrete capability (`capabilityKey`)
 *     and requests the binding signed quote against that exact SKU.
 *
 * It reads the amount + selected payment from the global store, so both the
 * TransferPage and the payment-method slot can call it and converge on the same
 * cached pre-quote.
 */
export const useTransferPricing = (): TransferPricing => {
  const { amountState, paymentState } = useStore();
  const { session } = useSessionStore();

  const route = resolveActiveRoute(
    session.data?.config,
    session.data?.userData,
  );
  const { sourceCountry, destinationCountry, sourceCurrency, destinationCurrency } =
    route;

  const payoutMethod = resolvePayoutMethod(session.data?.userData);
  const payerCode = resolvePayerCode(session.data?.userData);
  const anchor: QuoteAnchor = amountState.anchor ?? "send";

  const amount = useMemo(() => {
    const raw = amountState.amount?.replace(/,/g, "");
    const parsed = raw ? parseFloat(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [amountState.amount]);

  const preQuoteInput = useMemo<IPreQuoteRequest | null>(() => {
    if (!amount) return null;
    return {
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      amount,
      anchor,
      sourceCurrency,
      destinationCurrency,
      ...(payoutMethod ? { payoutMethod } : {}),
    };
  }, [
    amount,
    anchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
  ]);

  const {
    result: preQuote,
    loading: preQuoteLoading,
    error: preQuoteError,
  } = usePreQuote(preQuoteInput);

  const options = useMemo(
    () => filterPreQuoteOptions(preQuote?.options, payoutMethod, payerCode),
    [preQuote, payoutMethod, payerCode],
  );

  const fundingMethods = useMemo(
    () => availableFundingMethods(options),
    [options],
  );
  const categories = useMemo(
    () => availableFundingCategories(options),
    [options],
  );
  const bestOption = useMemo<IEstimateOption | null>(() => {
    if (options.length === 0) return null;
    return rankOptions(options, anchor)[0] ?? null;
  }, [options, anchor]);

  const optionForCategory = useMemo(
    () => (category: FundingCategory) => {
      const filtered = options.filter(
        (o) => categoryFromFundingMethod(o.fundingMethod) === category
      );
      return rankOptions(filtered, anchor)[0];
    },
    [options, anchor],
  );

  // Map the currently selected payment method to a concrete capability.
  const selectedCategory = useMemo<FundingCategory | undefined>(() => {
    const method = mapPaymentTypeToMethod(paymentState.payment?.type);
    return method ? categoryFromFundingMethod(method) : undefined;
  }, [paymentState.payment?.type]);

  const selectedOption = useMemo<IEstimateOption | null>(() => {
    if (!selectedCategory) return null;
    return optionForCategory(selectedCategory) ?? null;
  }, [optionForCategory, selectedCategory]);

  const selectedFundingMethod = selectedOption?.fundingMethod ?? null;
  const capabilityKey = selectedOption?.capabilityKey ?? null;

  // Claim may omit payoutMethod (masked / legacy links). The selected pre-quote
  // SKU still carries the canonical payout — without this fallback useQuote
  // never fires and the confirm CTA stays disabled while economics look fine.
  const quotePayoutMethod =
    payoutMethod ?? selectedOption?.payoutMethod ?? null;

  const cardId =
    paymentState.payment?.type === "CARD"
      ? paymentState.payment?.cardId ?? null
      : null;

  const {
    quote,
    loading: quoteLoading,
    error: quoteError,
    refetch: refetchQuote,
    isExpiringSoon,
    timeRemaining,
  } = useQuote({
    sourceCountry,
    destinationCountry,
    fundingMethod: selectedFundingMethod,
    payoutMethod: quotePayoutMethod,
    capabilityKey,
    amount,
    currency: sourceCurrency,
    destinationCurrency,
    anchor,
    cardId,
  });

  const economics = useMemo<TransferEconomics | null>(() => {
    if (quote) {
      return {
        sendAmount: quote.sendAmount,
        receiveAmount: quote.receiveAmount,
        exchangeRate: quote.exchangeRate,
        fee: quote.totalFee ?? quote.fee ?? 0,
        totalCost: quote.totalCost,
        estimated: false,
      };
    }
    const option = selectedOption ?? bestOption;
    if (option) {
      return {
        sendAmount: option.sendAmount,
        receiveAmount: option.receiveAmount,
        exchangeRate: option.customerFxRate,
        fee: option.customerFee,
        totalCost: option.sendAmount + option.customerFee,
        estimated: true,
      };
    }
    return null;
  }, [quote, selectedOption, bestOption]);

  return {
    route,
    payoutMethod,
    payerCode,
    amount,
    anchor,
    preQuoteLoading,
    preQuoteError,
    options,
    availableFundingMethods: fundingMethods,
    availableCategories: categories,
    optionForCategory,
    bestOption,
    selectedOption,
    selectedFundingMethod,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    isExpiringSoon,
    timeRemaining,
    economics,
  };
};
